import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { HelpHotspot } from '@/features/help/HelpHotspot';

interface SectionCardProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
  // When true, applies a subtle gradient background. Used for hero sections.
  hero?: boolean;
  // Topic key for the help drawer. When set, renders an inline (?) hotspot
  // next to the title so users can learn about this section in place.
  helpTopic?: string;
}

// Wraps content in a polished card with consistent header/body separation.
// Use for: agent detail sections, dashboard groupings, scenario detail.
//
// Compared to antd Card:
//  - Cleaner header (no border, just generous spacing)
//  - Optional hero gradient
//  - Tabular numerics inside body
//  - Accepts subtitle for two-line headers
//  - Hover state subtle (no big lift)

export const SectionCard = observer(function SectionCard({
  title,
  subtitle,
  actions,
  children,
  noPadding,
  hero,
  helpTopic,
}: SectionCardProps) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const bg = hero
    ? isDark
      ? `linear-gradient(135deg, ${colors.slate[800]}, ${colors.slate[700]})`
      : `linear-gradient(135deg, #FFFFFF, ${colors.ink[50]})`
    : isDark
    ? colors.slate[800]
    : '#FFFFFF';

  const border = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 12,
        boxShadow: hero
          ? '0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04)'
          : '0 1px 3px rgba(15,23,42,0.04)',
        overflow: 'hidden',
      }}
    >
      {(title || actions) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 18px 10px',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {title && (
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: isDark ? colors.slate[100] : colors.slate[900],
                  letterSpacing: '-0.005em',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {title}
                {helpTopic && (
                  <HelpHotspot
                    topic={helpTopic}
                    variant="inline"
                    label={typeof title === 'string' ? `Learn about ${title.toLowerCase()}` : 'Learn more'}
                  />
                )}
              </div>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: 12,
                  color: isDark ? colors.slate[400] : colors.slate[500],
                  marginTop: 2,
                  lineHeight: 1.5,
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
          {actions && <div style={{ display: 'flex', gap: 6 }}>{actions}</div>}
        </div>
      )}
      <div style={{ padding: noPadding ? 0 : '4px 18px 16px' }}>{children}</div>
    </div>
  );
});
